import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as yup from "yup";
import { Formik } from "formik";
import { Alert, Box, Button, FormControlLabel, FormHelperText, Grid, Radio, Typography } from "@mui/material";
import OZInput from "../../../components/admin/common/OZInput";
import OZSelect from "../../../components/admin/common/OZSelect";
import { Stack } from "@mui/system";
import SimpleSwitch from "../../../components/admin/common/OZSwitch/SimpleSwitch";
import TimePicker from "rc-time-picker";
import OzDatePicker from "../../../components/admin/common/OzDatePicker";
import moment from "moment";
import dayjs from "dayjs";
import { CloudUpload } from "@mui/icons-material";
import "./campaign.css";
import campaignApi from "./campaignApi";
import { getUserId } from "../../../services/auth-service";
import { templateApi } from "./templateApi";
import senderApi from "./senderApi";
import { showErrorNotification, showSuccessNotification } from "../../../components/admin/common/NotiService";
import { getSmsVendors } from "../../../services/api-service";
import { ScrollToFieldError } from "../../../components/admin/common/ScrollToFieldError";

// Constants
const INITIAL_CAMPAIGN_DATA = {
    name: '',
    type: 'sms',
    category: 'simple',
    messageType: 'Simple',
    vendor: '',
    sender: '',
    templateName: '',
    templateType: '',
    templateText: '',
    templateLanguage: '',
    allowDuplicate: false,
    numbers: '',
    schedule: {
        scheduled: false,
        startDate: null,
        endDate: null,
        start: "09:00:00",
        end: "20:59:59"
    },
    extraData: {},
};

const VALIDATION_SCHEMA = yup.object({
    name: yup
        .string()
        .min(2, "Campaign Name should be between 2 to 150 alphanumeric characters long allows special characters like _,space and Campaign Name cannot start and end with Special characters.")
        .max(150, "Campaign Name should be between 2 to 150 alphanumeric characters long allows special characters like _,space and Campaign Name cannot start and end with Special characters.")
        .matches(/^([A-Za-z0-9]((_|\s)*?[A-Za-z0-9])+)$/, 'Campaign Name should be between 2 to 150 alphanumeric characters long allows special characters like _,space and Campaign Name cannot start and end with Special characters.')
        .required("Campaign Name is required").nullable(),
    vendor: yup
        .string()
        .required("Vendor is required").nullable(),
    sender: yup
        .string()
        .required("Sender is required").nullable(),
    templateName: yup
        .string()
        .required("Template Name is required").nullable(),
    category: yup
        .string()
        .required("Category is required").nullable(),
    numbers: yup
        .string().nullable()
        .when("category", {
            is: "simple",
            then: yup.string().when("messageType", {
                is: "Simple",
                then: yup.string().required("Phone Numbers are required for simple category").nullable(),
                otherwise: yup.string().notRequired().nullable()
            }),
            otherwise: yup.string().notRequired().nullable()
        }),
});

// Utility functions
const convertToListFormat = (data, label) => {
    return data.map(item => item[label]);
};

// Sub-components
const CampaignHeader = ({ id, campaignName, onClose }) => (
    <Stack spacing={2}>
        <Typography
            component="div"
            variant="h6"
            sx={{
                fontFamily: 'Roboto',
                fontStyle: 'normal',
                fontWeight: 400,
                fontSize: '12px',
                lineHeight: '14px',
                color: '#212121',
                cursor: "pointer",
            }}
            onClick={onClose}
        >
            {`CXI Reach / ${!id ? 'Create' : 'SMS'} Campaign`}
        </Typography>
        <Typography
            component="div"
            variant="h6"
            sx={{
                fontFamily: 'Roboto',
                fontStyle: 'normal',
                fontWeight: 600,
                fontSize: '24px',
                lineHeight: '28px',
                color: '#000000',
            }}
        >
            {`${!id ? 'Create SMS Campaign' : campaignName}`}
        </Typography>
    </Stack>
);

const CampaignStats = ({ campaign }) => (
    <Grid container spacing={2} sx={{ marginBottom: 2 }}>
        <Grid item xs={12} sm={4}>
            <Alert severity="info" icon={false} action={<Typography sx={{ fontWeight: 'bold' }}>{campaign?.counts.uploaded || 0}</Typography>}>
                Uploaded
            </Alert>
        </Grid>
        <Grid item xs={12} sm={4}>
            <Alert severity="success" icon={false} action={<Typography sx={{ fontWeight: 'bold' }}>{campaign?.counts.sent || 0}</Typography>}>
                Sent
            </Alert>
        </Grid>
        <Grid item xs={12} sm={4}>
            <Alert severity="error" icon={false} action={<Typography sx={{ fontWeight: 'bold' }}>{(campaign?.counts.uploaded || 0) - (campaign?.counts.sent || 0)}</Typography>}>
                Pending
            </Alert>
        </Grid>
    </Grid>
);

const MessageTypeSection = ({ formik, disabled }) => (
    <>
        <Grid item xs={6} sm={6} md={6}>
            <OZSelect
                name="messageType"
                label="Upload Type *"
                options={["Simple", "Bulk"]}
                optionLabel="name"
                placeholder="Select Upload Type"
                formik={formik}
                disabled={disabled}
                OnSaveCallBack={(name, option, value) => {
                    if (value === 'Bulk') {
                        formik.setFieldValue('numbers', '');
                    }
                    if (value === 'Simple') {
                        formik.setFieldValue('fileUpload', null);
                    }
                }}
            />
        </Grid>
        <Grid item xs={6} sm={6} md={6}>
        </Grid>
    </>
);

const FileUploadSection = ({ formik, disabled, onFileChange }) => {
    const onDownload = () => {
        try {
            const link = document.createElement("a");
            link.download = "data_upload_cxi_campaign_sms_sample.xlsx";
            link.href = `${process.env.PUBLIC_URL}/sample_files/data_upload_cxi_campaign_sample.xlsx`;
            link.click();
        } catch (error) {
            showErrorNotification("Something went wrong with file download, kindly refresh the page and try again");
        }
    };

    return (
        <Grid item xs={12} sm={12}>
            {/* Label with download link on the right */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                <Typography
                    fontSize='12px'
                    fontWeight={400}
                    lineHeight='14px'
                    marginLeft='1px'
                    color={disabled ? '#99A0A8' : '#212121'}
                >
                    Upload File *
                </Typography>
                <Typography
                    variant="span"
                    sx={{
                        fontSize: '12px',
                        color: '#4B87FF',
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        '&:hover': {
                            color: '#3D78E6',
                        }
                    }}
                    onClick={onDownload}
                >
                    Download Sample
                </Typography>
            </div>

            <div style={{ position: 'relative' }}>
                <OZInput
                    name="fileUploadDisplay"
                    placeholder="Choose file..."
                    value={formik.values.fileUpload?.name || ""}
                    disabled={true}
                />
                <Button
                    component="label"
                    startIcon={<CloudUpload />}
                    disabled={disabled}
                    sx={{
                        position: 'absolute',
                        right: '8px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        textTransform: "none",
                        p: 1,
                        borderRadius: '8px',
                        color: disabled ? '#99A0A8' : '#4B87FF',
                        '&:hover': {
                            backgroundColor: disabled ? 'transparent' : 'rgba(75, 135, 255, 0.04)',
                        },
                    }}
                >
                    Upload
                    <input
                        type="file"
                        hidden
                        onChange={onFileChange}
                        accept=".csv,.xlsx,.xls"
                        disabled={disabled}
                    />
                </Button>
            </div>

            {formik.touched.fileUpload && formik.errors.fileUpload && (
                <FormHelperText error sx={{ marginLeft: '14px', marginTop: '3px' }}>
                    {formik.errors.fileUpload}
                </FormHelperText>
            )}
            <FormHelperText sx={{ marginLeft: '14px', marginTop: '3px', color: '#99A0A8' }}>
                Supported formats: CSV, Excel (.xlsx, .xls) - Max size: 5MB
            </FormHelperText>
        </Grid>
    );
};

const MessageSection = ({ formik, templates, disabled, mode }) => {

    const isTemplateDisabled = formik?.values?.category === 'dynamic' || disabled;

    // Message Preview Component
    const MessagePreview = ({ message }) => {
        const currentTime = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

        return (
            <Box sx={{
                // mt: 2,
                p: 2,
                // border: '1px solid #E0E0E0',
                borderRadius: '12px',
                // backgroundColor: '#F8F9FA'
            }}>
                {/* <Typography variant="subtitle2" sx={{
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#666',
                    mb: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                }}>
                    📱 Message Preview
                </Typography> */}

                <Box sx={{
                    maxWidth: '300px',
                    mx: 'auto',
                    border: '1px solid #E0E0E0',
                    borderRadius: '20px',

                }}>
                    {/* Phone screen */}
                    <Box sx={{
                        backgroundColor: '#fff',
                        borderRadius: '20px',
                        overflow: 'hidden',
                        minHeight: '200px'
                    }}>
                        {/* Status bar */}
                        <Box sx={{
                            backgroundColor: '#F8F9FA',
                            p: '8px 16px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            borderBottom: '1px solid #E0E0E0'
                        }}>
                            <Typography sx={{ fontSize: '12px', fontWeight: 600 }}>
                                Messages
                            </Typography>
                            <Typography sx={{ fontSize: '12px', color: '#666' }}>
                                {currentTime}
                            </Typography>
                        </Box>

                        {/* Message thread */}
                        <Box sx={{ p: '16px' }}>
                            {/* Sender info */}
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                mb: 2,
                                gap: 1
                            }}>
                                <Box sx={{
                                    width: '32px',
                                    height: '32px',
                                    borderRadius: '50%',
                                    backgroundColor: '#4B87FF',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'white',
                                    fontSize: '14px',
                                    fontWeight: 600
                                }}>
                                    {formik?.values?.sender ? formik?.values?.sender?.charAt(0).toUpperCase() : 'S'}
                                </Box>
                                <Typography sx={{
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    color: '#333'
                                }}>
                                    {formik?.values?.sender || 'Sender'}
                                </Typography>
                            </Box>

                            {/* Message bubble */}
                            <Box sx={{
                                backgroundColor: '#E3F2FD',
                                borderRadius: '18px 18px 18px 4px',
                                p: '12px 16px',
                                maxWidth: '85%',
                                wordBreak: 'break-word',
                                mb: 1
                            }}>
                                <Typography sx={{
                                    fontSize: '14px',
                                    lineHeight: 1.4,
                                    color: '#333',
                                    whiteSpace: 'pre-wrap'
                                }}>
                                    {message || 'Your message will appear here...'}
                                </Typography>
                            </Box>

                            {/* Message info */}
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                mt: 1
                            }}>
                                <Typography sx={{
                                    fontSize: '11px',
                                    color: '#999'
                                }}>
                                    {currentTime}
                                </Typography>
                                <Typography sx={{
                                    fontSize: '11px',
                                    color: '#999'
                                }}>
                                    {Math.ceil((message || '').length / 160)} part{Math.ceil((message || '').length / 160) !== 1 ? 's' : ''}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>
        );
    };

    return (
        <>
            <Grid item xs={6} sm={6}>
                <div>
                    <div style={{ display: 'flex', alignContent: 'center' }}>
                        <Typography sx={{
                            fontStyle: 'normal',
                            fontWeight: 400,
                            fontSize: '12px',
                            color: '#212121',
                        }} flexGrow={1}>
                            Message *
                        </Typography>
                    </div>
                    <div style={{ marginTop: '2px' }}>
                        <OZInput
                            name="templateText"
                            placeholder="Message"
                            multiline={true}
                            maxRows={1}
                            height='auto'
                            inputProps={{ style: { height: '150px' } }}
                            formik={formik}
                            disabled={isTemplateDisabled}
                            onChange={(e) => formik.setFieldValue("templateText", e.target.value)}
                        />
                        {formik.values.category === 'dynamic' && <FormHelperText disabled={true} >
                            Template cannot be edited for dynamic category
                        </FormHelperText>}
                    </div>
                    <FormHelperText sx={{ color: "#3D8BF8 !important" }}>
                        {`Total message characters ${formik.values.templateText.length} will be divided into ${Math.ceil(formik.values.templateText.length / 160)} parts${formik.values.category === 'dynamic' ? ', it may vary based on dynamic content' : ''}`}
                    </FormHelperText>


                </div>
            </Grid>
            {/* Message Preview */}
            <Grid item xs={6} sm={6} md={6}>
                <MessagePreview message={formik?.values?.templateText || ''} />
            </Grid>
        </>
    );
};

const ScheduleSection = ({ formik, disabled }) => {

    const isPromotional = formik.values.templateType === 'promotional';

    return (
        <Grid item xs={12} sm={12}>
            <Box style={{ border: '1px solid #E6E5E6', borderRadius: '8px', padding: '12px' }}>
                <Box style={{ display: 'flex', alignContent: 'center' }}>
                    <Typography sx={{
                        fontStyle: 'normal',
                        fontWeight: 400,
                        fontSize: '12px',
                        color: '#212121',
                    }} flexGrow={1}>
                        Schedule *
                    </Typography>
                    <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'end', fontSize: '12px' }}>
                        <SimpleSwitch
                            name="schedule.scheduled"
                            checked={formik.values.schedule.scheduled}
                            onChange={() => formik.setFieldValue('schedule.scheduled', !formik.values.schedule.scheduled)}
                            disabled={disabled}
                        />
                        <Typography variant="span" sx={{ paddingLeft: 1 }}>Date Schedule</Typography>
                    </Box>
                </Box>
                <Box sx={{ borderRadius: '8px', pt: 1 }}>
                    {/* Promotional SMS Time Restriction Alert */}
                    {formik.values.templateType === 'promotional' && (
                        <Alert severity="info" sx={{ mt: 1, mb: 1, borderRadius: '8px' }}>
                            Campaign hours are restricted to 09:00:00 - 20:59:59 for promotional SMS
                        </Alert>
                    )}

                    <Grid container spacing={2}>
                        {formik.values.schedule.scheduled && (
                            <>
                                <Grid item xs={6} sm={3}>
                                    <OzDatePicker
                                        placeholder='Start Date'
                                        name="schedule.startDate"
                                        formik={formik}
                                        label="Start Date"
                                        disableField={disabled}
                                    />
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <OzDatePicker
                                        placeholder='End Date'
                                        name="schedule.endDate"
                                        formik={formik}
                                        label="End Date"
                                        disableField={disabled}
                                    />
                                </Grid>
                            </>
                        )}
                        <Grid item xs={6} sm={3} >
                            <Typography
                                fontSize='12px'
                                fontWeight={400}
                                lineHeight='14px'
                                marginBottom={'6px'}
                                marginLeft='1px'
                                color="#212121"
                            >
                                Start Time*
                            </Typography>
                            <TimePicker
                                placeholder='Start Time'
                                name="schedule.start"
                                format='HH:mm:ss'
                                label="Start Time"
                                popupStyle={{
                                    borderRadius: "8px",
                                    overflow: "hidden",
                                    zIndex: '9999',
                                }}
                                value={formik.values.schedule.start ? moment(formik.values.schedule.start, 'HH:mm:ss') : null}
                                onChange={(e) => {
                                    const isValidDateObject = moment(e).isValid();
                                    if (!isValidDateObject) {
                                        showErrorNotification("Invalid Start Time");
                                        return;
                                    }
                                    formik.setFieldValue('schedule.start', moment(e).format("HH:mm:ss"))
                                }}
                                disabled={disabled}
                                disabledHours={() => isPromotional ? [0, 1, 2, 3, 4, 5, 6, 7, 8, 21, 22, 23] : []}
                            />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Typography
                                fontSize='12px'
                                fontWeight={400}
                                lineHeight='14px'
                                marginBottom={'6px'}
                                marginLeft='1px'
                                color="#212121"
                            >
                                End Time*
                            </Typography>
                            <TimePicker
                                placeholder='End Time'
                                name="schedule.end"
                                format={'HH:mm:ss'}
                                label="End Time"
                                popupStyle={{
                                    borderRadius: "8px",
                                    overflow: "hidden",
                                    zIndex: '9999',
                                }}
                                value={formik.values.schedule.end ? moment(formik.values.schedule.end, 'HH:mm:ss') : null}
                                onChange={(e) => {
                                    const isValidDateObject = moment(e).isValid();
                                    if (!isValidDateObject) {
                                        showErrorNotification("Invalid End Time");
                                        return;
                                    }
                                    formik.setFieldValue('schedule.end', moment(e).format("HH:mm:ss"))
                                }}
                                disabled={disabled}
                                disabledHours={() => isPromotional ? [0, 1, 2, 3, 4, 5, 6, 7, 8, 21, 22, 23] : []}
                            />
                        </Grid>
                    </Grid>
                </Box>
            </Box>
        </Grid>
    );
}
const PhoneNumbersSection = ({ formik }) => (
    <Grid item xs={12} sm={12}>
        <div>
            <div style={{ display: 'flex', alignContent: 'center' }}>
                <Typography sx={{
                    fontStyle: 'normal',
                    fontWeight: 400,
                    fontSize: '12px',
                    color: '#212121',
                }} flexGrow={1}>
                    Phone Numbers *
                </Typography>
            </div>
            <div style={{ marginTop: '2px' }}>
                <OZInput
                    name="numbers"
                    placeholder="Phone Numbers(comma separated) like 9704XXXX, 98XXXX210"
                    multiline={true}
                    maxRows={1}
                    height='auto'
                    inputProps={{ style: { height: '100px' } }}
                />
            </div>
        </div>
    </Grid>
);

const ActionButtons = ({ mode, disabled, onCancel, onSubmit, isSubmitting }) => {

    const formButtonStyle = useMemo(
        () => ({
            borderRadius: '8px',
            fontWeight: '400',
            fontSize: '14px',
        }),
        []
    );

    return (
        <Grid container spacing={2} pt={2} alignItems="end" justifyContent="end">
            <Grid item>
                <Button
                    sx={formButtonStyle}
                    type="button"
                    onClick={onCancel}
                    style={formButtonStyle}
                    disabled={isSubmitting}
                    variant="outlined"
                >
                    Cancel
                </Button>
            </Grid>
            <Grid item>
                <Button
                    onClick={onSubmit}
                    disabled={disabled || isSubmitting}
                    style={formButtonStyle}
                    variant="contained"
                >
                    {isSubmitting ? 'Saving...' : (mode === 'edit' ? 'Update' : 'Save')}
                </Button>
            </Grid>
        </Grid>
    );
};

// Custom hook for data fetching
const useCampaignData = (userId, campaignId) => {
    const [campaign, setCampaign] = useState(INITIAL_CAMPAIGN_DATA);
    const [senders, setSenders] = useState([]);
    const [allTemplates, setAllTemplates] = useState([]);
    const [loadingTemplates, setLoadingTemplates] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            setLoadingTemplates(true);
            try {
                if (campaignId) {
                    const campaignResponse = await campaignApi.getCampaign(userId, campaignId);
                    if (campaignResponse && typeof campaignResponse === "object" && campaignResponse.status === "success") {
                        let data = campaignResponse?.data;
                        if (data && typeof data === "object") {
                            if (data.schedule) {
                                if (data.schedule.startDate) data.schedule.startDate = dayjs(data.schedule.startDate);
                                if (data.schedule.endDate) data.schedule.endDate = dayjs(data.schedule.endDate);
                                console.log('data', data.schedule)
                            }
                        }
                        setCampaign(data);
                    } else {
                        throw new Error(campaignResponse?.message || "Something went wrong with campaign listing, kindly try again later");
                    }
                }

                const [sendersResponse, templatesResponse] = await Promise.all([
                    senderApi.listSenders(userId, 0, 100),
                    templateApi.listSMSTemplates(userId, 0, 100)
                ]);

                setSenders(sendersResponse.data.content);
                setAllTemplates(templatesResponse.data.content);
            } catch (error) {
                console.error('Error fetching campaign data:', error);
            } finally {
                setLoadingTemplates(false);
            }
        };

        fetchData();
    }, [userId, campaignId]);

    return { campaign, senders, allTemplates, loadingTemplates };
};

const useSmsVendors = () => {
    const [smsVendors, setSmsVendors] = useState([]);
    const [loadingVendors, setLoadingVendors] = useState(false);

    useEffect(() => {
        const controller = new AbortController();
        const fetchSmsVendors = async (controller) => {
            setLoadingVendors(true);
            try {
                const response = await getSmsVendors(controller);
                if (response?.status === "success" && response?.data && Array.isArray(response?.data)) {
                    const vendors = [];
                    if (response?.data?.length > 0) {
                        response?.data?.forEach(item => {
                            if (item.sms_provider) {
                                vendors.push(item.sms_provider);
                            }
                        });
                    }
                    setSmsVendors(vendors);
                }
            } catch (error) {
                console.error("Failed to fetch SMS vendors:", error);
            } finally {
                setLoadingVendors(false);
            }
        };
        fetchSmsVendors(controller);
        return () => controller.abort();
    }, []);
    return { smsVendors, loadingVendors };
};

const SmsTemplateForm = ({ formik, disabled, campaign, mode, handleCancel, isSubmitting, smsVendors, loadingVendors, senders, allTemplates, loadingTemplates }) => {

    const senderValue = useMemo(() => {
        try {
            return formik.values.sender;
        } catch (error) {
            return null;
        }
    }, [formik?.values]);

    const templateListBySender = useMemo(() => {
        try {
            return allTemplates.filter(template => template?.sender === senderValue);
        } catch (error) {
            return [];
        }
    }, [allTemplates, senderValue]);


    const handleFileChange = useCallback((event, formik) => {
        const file = event.target.files?.[0];
        if (file) {
            // Validate file size (5MB limit)
            const maxSize = 5 * 1024 * 1024; // 5MB
            if (file.size > maxSize) {
                showErrorNotification("File size must be less than 5MB");
                return;
            }

            // Validate file type
            const allowedTypes = ['.csv', '.xlsx', '.xls'];
            const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
            if (!allowedTypes.includes(fileExtension)) {
                showErrorNotification("Please upload a valid CSV or Excel file");
                return;
            }

            formik.setFieldValue("fileUpload", file);
        }
    }, []);


    return (
        <div>
            <Stack spacing={2} sx={{ padding: 2, my: 1.5, backgroundColor: '#fff', width: '65vw', borderRadius: '8px' }}>

                {disabled && (
                    <Alert severity="info">
                        Campaign Edit is disabled for completed or running campaigns.
                    </Alert>
                )}

                <form id="smsCampaignForm" onSubmit={formik.handleSubmit}>
                    {formik.values?.id && <CampaignStats campaign={campaign} />}

                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <OZInput
                                name="name"
                                placeholder="Enter Campaign Name"
                                label="Campaign Name *"
                                disabled={disabled}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <OZInput
                                name="callbackUrl"
                                placeholder="Callback URL"
                                label="Callback URL"
                                disabled={disabled}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <OZSelect
                                name="vendor"
                                label="Vendor *"
                                options={smsVendors}
                                placeholder="Select Vendor"
                                formik={formik}
                                disabled={disabled}
                                loading={loadingVendors}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <OZSelect
                                name="sender"
                                label="Sender *"
                                options={convertToListFormat(senders, "name")}
                                optionLabel="name"
                                placeholder="Select Sender"
                                formik={formik}
                                disabled={disabled}
                                loading={loadingTemplates}
                                OnSaveCallBack={(name, option, value) => {
                                    formik.setFieldValue("templateName", '');
                                    formik.setFieldValue("templateType", '');
                                    formik.setFieldValue("templateText", '');
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <OZSelect
                                name="templateName"
                                label="Template Name *"
                                options={convertToListFormat(templateListBySender, "name")}
                                optionLabel="name"
                                placeholder="Select Template Name"
                                formik={formik}
                                disabled={disabled || !senderValue}
                                loading={loadingTemplates}
                                OnSaveCallBack={(name, option, value) => {
                                    try {
                                        if (value) {
                                            const template = allTemplates.find(template => template.name === value);
                                            if (template) {
                                                formik.setFieldValue("templateType", template.type);
                                                formik.setFieldValue("templateText", template.text);
                                            }
                                        }
                                    } catch (error) {
                                        console.error("Error setting template type:", error);
                                    }
                                }}
                            />
                            {formik.values.templateType && (
                                <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block', fontSize: '12px', fontWeight: 400, textTransform: 'capitalize' }}>
                                    {(() => {
                                        return `Type: ${formik.values.templateType}`;
                                    })()}
                                </Typography>
                            )}
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <OZSelect
                                name="category"
                                label="Template Category *"
                                options={['simple', 'dynamic']}
                                placeholder="Select Category"
                                formik={formik}
                                disabled={disabled || mode === 'edit'}
                            />
                        </Grid>
                        <MessageSection formik={formik} templates={allTemplates} disabled={disabled} mode={mode} />
                        <ScheduleSection formik={formik} disabled={disabled} />
                        {/* Message Type Section - only for new campaigns */}
                        {mode !== 'edit' && (
                            <MessageTypeSection formik={formik} disabled={disabled} />
                        )}
                        {!formik.values?.id && formik.values.messageType === 'Simple' && <PhoneNumbersSection formik={formik} />}
                        {mode !== 'edit' && formik.values.messageType === 'Bulk' && (
                            <FileUploadSection formik={formik} disabled={disabled} onFileChange={(e) => handleFileChange(e, formik)} />
                        )}
                    </Grid>
                    <Grid item xs={12} sm={12} mt={2}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'start', fontSize: '12px' }}>
                            <SimpleSwitch
                                name="allowDuplicate"
                                checked={formik.values.allowDuplicate}
                                onChange={() => formik.setFieldValue("allowDuplicate", !formik.values.allowDuplicate)}
                                disabled={disabled}
                            />
                            <Typography variant="span" sx={{ paddingLeft: 1 }}>Allow Duplicate</Typography>
                        </div>
                    </Grid>
                    <ActionButtons
                        mode={mode}
                        disabled={disabled}
                        onCancel={handleCancel}
                        onSubmit={() => {
                            if (formik.isValid) {
                                formik.handleSubmit()
                            } else {
                                console.log('formik error in ActionButtons OnSubmit', formik.errors)
                                formik.validateForm()
                            }
                        }}
                        isSubmitting={isSubmitting}
                    />
                    <ScrollToFieldError />
                </form>
            </Stack>
        </div>
    );
};

// Main component
const CxiSmsCampaignForm = ({ id, onClose, refreshData }) => {
    const mode = id ? 'edit' : 'create';
    const [userId] = useState(getUserId() || null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { campaign, senders, allTemplates, loadingTemplates } = useCampaignData(userId, id);
    const { smsVendors, loadingVendors } = useSmsVendors();
    const [loading, setLoading] = useState(false);

    const disabled = ['completed', 'running'].includes(campaign?.position || '');

    const handleSubmit = async (values, formik) => {
        setLoading(true);
        console.log("🚀 ~ handleSubmit ~ values:", values)
        console.log("🚀 ~ handleSubmit ~ startDate before processing:", values?.schedule?.startDate)
        console.log("🚀 ~ handleSubmit ~ endDate before processing:", values?.schedule?.endDate)

        let formData = { ...values };
        if (formData?.schedule?.startDate === "Invalid date") formData.schedule.startDate = null;
        if (formData?.schedule?.endDate === "Invalid date") formData.schedule.endDate = null;

        if (formData && typeof formData === 'object' && "schedule" in formData && typeof formData.schedule === 'object') {
            if ('startDate' in formData.schedule) {
                // Handle dayjs object from OzDatePicker
                let isValidDate = false;
                let formattedStartDate = null;

                if (formData.schedule.startDate) {
                    // Check if it's a dayjs object
                    if (dayjs.isDayjs(formData.schedule.startDate)) {
                        isValidDate = formData.schedule.startDate.isValid();
                        formattedStartDate = isValidDate ? formData.schedule.startDate.format("YYYY-MM-DD") : null;
                        console.log("🚀 ~ startDate is dayjs object, formatted:", formattedStartDate);
                    } else {
                        // Fallback to moment for other date formats
                        isValidDate = moment(formData.schedule.startDate).isValid();
                        formattedStartDate = isValidDate ? moment(formData.schedule.startDate).format("YYYY-MM-DD") : null;
                        console.log("🚀 ~ startDate is not dayjs, formatted with moment:", formattedStartDate);
                    }
                }

                if (!isValidDate && "scheduled" in formData.schedule && formData.schedule.scheduled) {
                    showErrorNotification("Invalid Start Date");
                    return;
                }
                formData.schedule.startDate = formattedStartDate;
            }
            if ('endDate' in formData.schedule) {
                // Handle dayjs object from OzDatePicker
                let isValidDate = false;
                let formattedEndDate = null;

                if (formData.schedule.endDate) {
                    // Check if it's a dayjs object
                    if (dayjs.isDayjs(formData.schedule.endDate)) {
                        isValidDate = formData.schedule.endDate.isValid();
                        formattedEndDate = isValidDate ? formData.schedule.endDate.format("YYYY-MM-DD") : null;
                        console.log("🚀 ~ endDate is dayjs object, formatted:", formattedEndDate);
                    } else {
                        // Fallback to moment for other date formats
                        isValidDate = moment(formData.schedule.endDate).isValid();
                        formattedEndDate = isValidDate ? moment(formData.schedule.endDate).format("YYYY-MM-DD") : null;
                        console.log("🚀 ~ endDate is not dayjs, formatted with moment:", formattedEndDate);
                    }
                }

                if (!isValidDate && "scheduled" in formData.schedule && formData.schedule.scheduled) {
                    showErrorNotification("Invalid End Date");
                    return;
                }
                formData.schedule.endDate = formattedEndDate;
            }
            const start = moment(formData.schedule.start, 'HH:mm:ss');
            const end = moment(formData.schedule.end, 'HH:mm:ss');
            if (end.isBefore(start)) {
                showErrorNotification("End time must be after start time");
                return;
            }

            if (formData.templateType === 'promotional') {
                const start = moment(formData.schedule.start, 'HH:mm:ss');
                const end = moment(formData.schedule.end, 'HH:mm:ss');
                if (start.isBefore(moment('09:00:00', 'HH:mm:ss')) || end.isAfter(moment('20:59:59', 'HH:mm:ss'))) {
                    showErrorNotification("Start time must be between 09:00:00 and 20:59:59 for promotional SMS");
                    return;
                }
            }
        }
        const formDataToAdd = new FormData();

        if (formData.messageType === 'Bulk') {
            if (formData.fileUpload) {
                formDataToAdd.append('file', formData.fileUpload);
            }
            delete formData.numbers;
        }

        delete formData.messageType;
        delete formData.fileUpload;
        delete formData.templateLanguage


        formDataToAdd.append('campaign', new Blob([JSON.stringify(formData)], {
            type: 'application/json'
        }));

        console.log("🚀 ~ handleSubmit ~ handleSubmit:", formData)
        setIsSubmitting(true);
        try {
            let response;
            if (mode === 'edit') {
                response = await campaignApi.updateCampaign(userId, id, formData); // for edit we can directly send formData
            } else {
                response = await campaignApi.createCampaign(userId, formData, formDataToAdd);
            }
            if (response && typeof response === "object" && response.status === "success") {
                showSuccessNotification(response?.message || "Campaign created successfully");
                if (refreshData) {
                    refreshData();
                }
                if (onClose) {
                    onClose();
                }
            } else {
                showErrorNotification(response?.message || "Failed to create campaign");
            }

        } catch (error) {
            console.error('Error saving campaign:', error);
            showErrorNotification(
                error.response?.data?.message ||
                `Failed to ${mode === 'edit' ? 'update' : 'create'} campaign`
            );
        } finally {
            setIsSubmitting(false);
            setLoading(false);
        }
    };

    const handleCancel = () => {
        if (onClose) {
            onClose();
        }
    };

    return (
        <div className="cxi-sms-campaign-form">
            <CampaignHeader id={id} campaignName={campaign?.name} onClose={onClose} />

            <Formik
                initialValues={campaign}
                validationSchema={VALIDATION_SCHEMA}
                enableReinitialize
                onSubmit={handleSubmit}
            >
                {(formik) => (
                    <>
                        <SmsTemplateForm
                            formik={formik}
                            disabled={disabled}
                            campaign={campaign}
                            mode={mode}
                            handleCancel={handleCancel}
                            isSubmitting={isSubmitting}
                            smsVendors={smsVendors}
                            loadingVendors={loadingVendors}
                            senders={senders}
                            allTemplates={allTemplates}
                            loadingTemplates={loadingTemplates}
                        />
                    </>
                )}
            </Formik>
        </div>
    );
};

export default CxiSmsCampaignForm;